import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { updateModuleProgress } from '../../../SkillsApi/SkillsApi'
import './AiModuleFive.css'

const MODULE_ID = 5
const SECTION = 'AiSection'

// TODO: swap for the actual AI Module 5 video URL
const VIDEO_URL = 'https://res.cloudinary.com/dk50cmtps/video/upload/v1780034028/drone_yzbva8.mp4'

// TODO: swap these for questions that match AI Module 5's actual video content —
// these are placeholders on general AI/ML topics, same Tanglish style as Module 1-4.
const ASSESSMENT_QUESTIONS = [
  {
    id: 'q1',
    question: 'AI ethics-ல "explainability"-ஐ எப்படி define பண்றாங்க?',
    options: [
      { key: 'a', label: "Making a model's decisions understandable to humans" },
      { key: 'b', label: 'Making the model file smaller' },
      { key: 'c', label: 'Increasing the training speed' },
      { key: 'd', label: 'Adding more GPUs to a cluster' },
    ],
    correctKey: 'a',
  },
  {
    id: 'q2',
    question: 'Retrieval-Augmented Generation (RAG)-ன் முக்கிய நோக்கம் என்ன?',
    options: [
      { key: 'a', label: 'Grounding model responses with retrieved external data' },
      { key: 'b', label: 'Compressing video files' },
      { key: 'c', label: 'Speeding up CPU clock cycles' },
      { key: 'd', label: 'Encrypting user sessions' },
    ],
    correctKey: 'a',
  },
  {
    id: 'q3',
    question: 'Model "fine-tuning" pretraining-ல இருந்து எப்படி வேறுபடுது?',
    options: [
      { key: 'a', label: 'It adapts an already-trained model to a specific task with less data' },
      { key: 'b', label: 'It trains a model completely from scratch every time' },
      { key: 'c', label: 'It only changes the model file name' },
      { key: 'd', label: 'It removes all model layers' },
    ],
    correctKey: 'a',
  },
]

const AiModuleFive = () => {
  const navigate = useNavigate()
  const [answers, setAnswers] = useState({})
  const [watchPercent, setWatchPercent] = useState(0)
  const [videoEnded, setVideoEnded] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const lastSentMilestoneRef = useRef(0)

  useEffect(() => {
    document.querySelector('#root')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Same right-click / DevTools-shortcut deterrent as AI Module 1-4.
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault()
      toast.warn('Right-click is disabled on this page.')
    }

    const handleKeyDown = (e) => {
      const isDevToolsShortcut =
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C', 'i', 'j', 'c'].includes(e.key)) ||
        (e.ctrlKey && ['U', 'u'].includes(e.key))

      if (isDevToolsShortcut) {
        e.preventDefault()
        toast.warn('Inspect is disabled on this page.')
      }
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const sendMilestone = async (milestone) => {
    if (milestone <= lastSentMilestoneRef.current) return
    lastSentMilestoneRef.current = milestone
    await updateModuleProgress(SECTION, MODULE_ID, milestone)
  }

  // Milestones capped at 90 during playback — the final 100% is only sent
  // once the assessment is finished too (see handleFinish), so AI Module 6
  // only unlocks once BOTH the video and the quiz are done.
  const handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.target
    if (!duration) return

    const percent = Math.min(100, Math.floor((currentTime / duration) * 100))
    setWatchPercent((prev) => (percent > prev ? percent : prev))

    const crossedMilestone = Math.min(90, Math.floor(percent / 10) * 10)
    if (crossedMilestone > lastSentMilestoneRef.current) {
      sendMilestone(crossedMilestone)
    }
  }

  const handleVideoEnded = () => {
    setWatchPercent(100)
    setVideoEnded(true)
    toast.info('Video watched! Now complete the assessment below.')
  }

  const handleAnswerSelect = (questionId, optionKey, correctKey) => {
    if (answers[questionId]) return

    setAnswers((prev) => ({ ...prev, [questionId]: optionKey }))

    if (optionKey === correctKey) {
      toast.success('Correct answer!')
    } else {
      toast.error('Wrong answer!')
    }
  }

  const allQuestionsAnswered = ASSESSMENT_QUESTIONS.every((q) => answers[q.id])
  const canFinish = videoEnded && allQuestionsAnswered

  const handleFinish = async () => {
    if (!canFinish || finishing) return
    setFinishing(true)
    await sendMilestone(100)
    toast.success('Module 5 complete — Module 6 unlocked!')
    navigate('/ai_course')
  }

  return (
    <div className="AiModuleFive_wrapper">
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="AiModuleFive_content">
        <h2 className="AiModuleFive_title">Module 5: Deep Dive</h2>
        <p className="AiModuleFive_subtitle">
          Watch the full video, then complete the short assessment to unlock Module 6.
        </p>

        <div className="AiModuleFive_video_wrap">
          <video
            className="AiModuleFive_video"
            src={VIDEO_URL}
            controls
            controlsList="nodownload"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        </div>

        <div className="AiModuleFive_progress_row">
          <div className="AiModuleFive_progress_track">
            <div
              className="AiModuleFive_progress_fill"
              style={{ width: `${watchPercent}%` }}
            />
          </div>
          <span className="AiModuleFive_progress_label">{watchPercent}% watched</span>
        </div>

        <div className="AiModuleFive_assessment">
          <h3 className="AiModuleFive_assessment_title">Module Assessment</h3>
          <p className="AiModuleFive_assessment_sub">
            {videoEnded
              ? `Answer all ${ASSESSMENT_QUESTIONS.length} questions to complete this module.`
              : 'Finish watching the video above to unlock the assessment.'}
          </p>

          {ASSESSMENT_QUESTIONS.map((q, qIndex) => {
            const selected = answers[q.id]

            return (
              <div className="AiModuleFive_question_card" key={q.id}>
                <p className="AiModuleFive_question_text">
                  <span className="AiModuleFive_question_num">Q{qIndex + 1}.</span> {q.question}
                </p>

                <div className="AiModuleFive_options_grid">
                  {q.options.map((opt) => {
                    const isSelected = selected === opt.key
                    const isCorrectOpt = opt.key === q.correctKey
                    let stateClass = ''

                    if (selected) {
                      if (isSelected && isCorrectOpt) stateClass = 'AiModuleFive_option_correct'
                      else if (isSelected && !isCorrectOpt) stateClass = 'AiModuleFive_option_wrong'
                      else if (!isSelected && isCorrectOpt) stateClass = 'AiModuleFive_option_reveal'
                    }

                    return (
                      <button
                        type="button"
                        key={opt.key}
                        className={`AiModuleFive_option_btn ${stateClass}`}
                        onClick={() => handleAnswerSelect(q.id, opt.key, q.correctKey)}
                        disabled={!videoEnded || !!selected}
                      >
                        <span className="AiModuleFive_option_key">{opt.key.toUpperCase()}</span>
                        <span className="AiModuleFive_option_label">{opt.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}

          <button
            type="button"
            className="AiModuleFive_complete_btn"
            onClick={handleFinish}
            disabled={!canFinish || finishing}
          >
            {finishing ? 'Saving...' : (
              <>
                Complete Module <span className="AiModuleFive_complete_check">&#10003;</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AiModuleFive